import React, { useState, useMemo } from 'react';
import { Modal, DatePicker, Divider, Button as AntdButton } from 'antd';
import Button from '../atoms/Button';
import InputSelect from '../atoms/InputSelect';
import { useGetStatusLookupsQuery } from '../../store/services/business-requests';
import { useGetBusinessTypesQuery } from '../../store/services/lookups';
import useQueryParams from '../../hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Filter, CloseCircle } from 'iconsax-react';
import CustomCheckbox from '../atoms/CustomCheckbox';
import YadLogo from '../../assets/icons/YadLogo';

const StatusFilters = () => {
  const { t } = useTranslation();
  const { data: requestStatuses } = useGetStatusLookupsQuery();
  const { data: businessTypes } = useGetBusinessTypesQuery();
  const { ValidQuery, replaceQueryParams } = useQueryParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusVal, setStatusVal] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<dayjs.Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<dayjs.Dayjs | null>(null);
  const [customerTypes, setCustomerTypes] = useState<string[]>([]);

  // Count selected filters in modal (updates as user selects/deselects)
  const currentSelectionCount = useMemo(() => {
    let count = 0;
    if (statusVal) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    if (customerTypes.length > 0) count++;
    return count;
  }, [statusVal, dateFrom, dateTo, customerTypes]);

  const handleOpenModal = () => {
    const query = ValidQuery as {
      statusCode?: string;
      fromDate?: string;
      toDate?: string;
      profileTypeCode?: string;
    };
    setStatusVal(query?.statusCode || null);
    setDateFrom(query?.fromDate ? dayjs(query.fromDate) : null);
    setDateTo(query?.toDate ? dayjs(query.toDate) : null);
    setCustomerTypes(
      query?.profileTypeCode ? query.profileTypeCode.split(',') : []
    );
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleClearForm = () => {
    setStatusVal(null);
    setDateFrom(null);
    setDateTo(null);
    setCustomerTypes([]);
  };

  const handleSaveFilters = () => {
    const set: { name: string; value: string }[] = [
      { name: 'page', value: '0' },
    ];
    const toDelete: string[] = [];

    if (statusVal) {
      set.push({ name: 'statusCode', value: statusVal });
    } else {
      toDelete.push('statusCode');
    }

    if (dateFrom) {
      set.push({ name: 'fromDate', value: dateFrom.format('YYYY-MM-DD') });
    } else {
      toDelete.push('fromDate');
    }

    if (dateTo) {
      set.push({ name: 'toDate', value: dateTo.format('YYYY-MM-DD') });
    } else {
      toDelete.push('toDate');
    }

    if (customerTypes.length > 0) {
      set.push({ name: 'profileTypeCode', value: customerTypes.join(',') });
    } else {
      toDelete.push('profileTypeCode');
    }

    replaceQueryParams({ delete: toDelete, set });
    handleCloseModal();
  };

  const handleCustomerTypeChange = (type: string, checked: boolean) => {
    if (checked) {
      setCustomerTypes([...customerTypes, type]);
    } else {
      setCustomerTypes(customerTypes.filter((t) => t !== type));
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleOpenModal}
        className="flex items-center gap-2 h-10 px-4 text-sm font-medium rounded mb-1 border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
        data-test="filter-btn"
      >
        <Filter size={18} />
        <span>{t('home.filter')}</span>
      </button>

      <Modal
        open={isModalOpen}
        onCancel={handleCloseModal}
        closable={false}
        footer={null}
        centered
        width={500}
        data-test="filter-modal"
      >
        <div className="flex flex-col gap-4" data-test="filter-modal-content">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <YadLogo width={50} height={50} />
              <div>
                <h2 className="text-xl font-bold">{t('home.filter')}</h2>
                <p className="text-sm text-gray-500">
                  {currentSelectionCount} {t('home.filterApplied')}{' '}
                  <span
                    onClick={handleClearForm}
                    className="text-light-success cursor-pointer hover:underline"
                    data-test="filter-reset-btn"
                  >
                    {t('home.resetFilter')}
                  </span>
                </p>
              </div>
            </div>
            <AntdButton
              type="text"
              variant="text"
              onClick={handleCloseModal}
              className="!p-1.5 !min-w-0 rounded hover:!bg-gray-100"
              icon={
                <CloseCircle
                  size={20}
                  variant="Bold"
                  className="text-light-text-primary"
                />
              }
              data-test="filter-modal-close-btn"
            />
          </div>

          {/* Customer Type */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-semibold">{t('home.customerType')}</h3>
                <p className="text-sm text-gray-500">
                  {t('home.customerTypeDesc')}
                </p>
              </div>
            </div>
            <div
              className="grid grid-cols-2 gap-8 mt-3"
              data-test="filter-customer-types"
            >
              {(Array.isArray(businessTypes) ? businessTypes : []).map((item) => {
                const code = item.code;
                const label = item.name;
                return (
                  <CustomCheckbox
                    key={code}
                    value={customerTypes.includes(code)}
                    onChange={(e) =>
                      handleCustomerTypeChange(code, e.target.checked)
                    }
                    data-test={`filter-customer-type-${code.toLowerCase()}-checkbox`}
                  >
                    {label}
                  </CustomCheckbox>
                );
              })}
            </div>
          </div>

          <Divider className="my-2" />

          {/* Status */}
          <div>
            <h3 className="font-semibold mb-2">{t('home.status')}</h3>
            <InputSelect
              id="status-select"
              placeholder={t('home.selectStatus')}
              value={statusVal}
              onChange={(val) => setStatusVal(val as string | null)}
              options={requestStatuses?.payload?.map((item) => ({
                label: item.name,
                value: item.code,
              }))}
              className="!bg-transparent"
            />
          </div>

          <Divider className="my-2" />
          {/* Date Range */}
          <div className="flex gap-4">
            <div className="flex-1">
              <h3 className="font-semibold mb-2">{t('home.dateFrom')}</h3>
              <DatePicker
                value={dateFrom}
                onChange={(date) => setDateFrom(date)}
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                className="w-full h-10"
                disabledDate={(current) =>
                  !current
                    ? false
                    : current.isAfter(dayjs(), 'day') ||
                      (!!dateTo && current.isAfter(dateTo, 'day'))
                }
              />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold mb-2">{t('home.dateTo')}</h3>
              <DatePicker
                value={dateTo}
                onChange={(date) => setDateTo(date)}
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                className="w-full h-10"
                disabledDate={(current) =>
                  !current
                    ? false
                    : current.isAfter(dayjs(), 'day') ||
                      (!!dateFrom && current.isBefore(dateFrom, 'day'))
                }
              />
            </div>
          </div>

          {/* Buttons */}
          <div
            className="flex justify-end gap-3 mt-4"
            data-test="filter-modal-actions"
          >
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
              data-test="filter-modal-cancel-btn"
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSaveFilters}
              className="w-40"
              data-test="filter-modal-save-btn"
            >
              {t('home.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StatusFilters;
